import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { onAuthStateChanged, signOut, User } from "firebase/auth";
import { auth } from "./firebase";
import { BreadcrumbProvider } from "./context/BreadcrumbContext";
import { PreviewModeProvider, usePreviewMode } from "./context/PreviewModeContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { applyTheme, loadStoredTheme } from "./themes";
import { useTranslation } from "react-i18next";
import HomePage from "./pages/HomePage";
import WelcomePage from "./pages/WelcomePage";
import AboutPage from "./pages/AboutPage";
import CreateLeaguePage from "./pages/CreateLeaguePage";
import JoinLeaguePage from "./pages/JoinLeaguePage";
import PublicJoinPage from "./pages/PublicJoinPage";
import LeaguePage from "./pages/LeaguePage";
import AdminLayout from "./pages/admin/AdminLayout";
import CompetitionsPage from "./pages/admin/CompetitionsPage";
import CreateCompetitionPage from "./pages/admin/CreateCompetitionPage";
import OpsHomePage from "./pages/admin/OpsHomePage";
import DevPage from "./pages/admin/DevPage";
import ClubsPage from "./pages/admin/ClubsPage";
import MetricsPage from "./pages/admin/MetricsPage";
import LeaguesPage from "./pages/admin/LeaguesPage";
import LeagueDetailPage from "./pages/admin/LeagueDetailPage";
import UsersTab from "./components/admin/UsersTab";
import ProfilePage from "./pages/ProfilePage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import TeamPage from "./pages/TeamPage";
import PredictionPage from "./pages/PredictionPage";
import GwChallengePage from "./pages/GwChallengePage";
import SyndicatePage from "./pages/SyndicatePage";
import GwChallengeTeamPage from "./pages/GwChallengeTeamPage";
import GwPickPage from "./pages/GwPickPage";
import AdminCompetitionPage, {
  OverviewRoute, GameweeksRoute, GameweekOpsRoute, EntrantsRoute, SquadsRoute, DataSourceRoute, ModesRoute,
} from "./pages/AdminCompetitionPage";
import MyGameweeksPage, { MyGameweekOpsRoute } from "./pages/MyGameweeksPage";
import ArchivePage from "./pages/ArchivePage";
import ScorePredictorPage from "./pages/ScorePredictorPage";
import Layout from "./components/Layout";
import AuthModal, { AuthModalMode } from "./components/AuthModal";
import WaitlistForm from "./components/WaitlistForm";
import { card, secondaryBtn } from "./styles";
import { captureAttribution, getStoredAttribution } from "./lib/attribution";

// Apply stored theme before first paint
applyTheme(loadStoredTheme());
// Record first-touch attribution (if any) before anything else runs, so it's ready the
// moment a sign-up actually happens — including a direct /join/:code landing.
captureAttribution();

function AppContent() {
  const { t } = useTranslation();
  const { previewMode } = usePreviewMode();
  const { setTheme } = useTheme();
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasDelegateAccess, setHasDelegateAccess] = useState(false);
  const [dbDisplayName, setDbDisplayName] = useState("");
  const [, setActiveLeagueCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isSuspended, setIsSuspended] = useState(false);
  const [isRegistrationClosed, setIsRegistrationClosed] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<AuthModalMode | null>(null);
  // Set when AuthModal reports a successful sign-in/register. `user` isn't
  // populated yet at that point (onAuthStateChanged awaits /users/sync
  // first), so WelcomePage watches this + `user` together and redirects to
  // /overview only once auth has actually resolved — never on its own,
  // since a signed-in user browsing to / (e.g. via the logo) must NOT be
  // force-redirected away from the marketing page.
  const [pendingDashboardRedirect, setPendingDashboardRedirect] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          for (let attempt = 0; attempt < 3; attempt++) {
            const token = await firebaseUser.getIdToken(attempt > 0);
            const resp = await fetch(`${process.env.REACT_APP_API_URL}/users/sync`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                email: firebaseUser.email,
                name: firebaseUser.displayName,
                displayName: firebaseUser.displayName,
                attribution: getStoredAttribution(),
              }),
            });
            if (resp.ok) {
              const dbUser = await resp.json();
              setIsAdmin(dbUser.role === "ADMIN");
              setHasDelegateAccess(!!dbUser.hasDelegateAccess);
              setDbDisplayName(dbUser.displayName ?? "");
              setIsSuspended(false);
              // Theme sync from DB disabled — re-enable when theme selector is reintroduced.
              // if (dbUser.theme) { setTheme(dbThemeToThemeId(dbUser.theme)); }
              break;
            }
            if (resp.status === 403) {
              const body = await resp.json().catch(() => ({}));
              if (body.code === "REGISTRATION_CLOSED") {
                setIsRegistrationClosed(true);
              } else {
                setIsSuspended(true);
              }
              break;
            }
            // Fresh registrations: createUserWithEmailAndPassword fires this listener
            // before updateProfile's displayName has propagated to the user object,
            // so the first sync 400s with "Missing required fields". Reload the user
            // and retry rather than rendering the app with no DB record yet (which
            // leaves /me, /leagues, /stats stuck on 404 until a manual refresh).
            // Note: don't guard on !firebaseUser.displayName — if updateProfile
            // completes while the first request is in-flight, displayName becomes
            // truthy before we check, but the sync already failed and needs a retry.
            if (resp.status === 400 && attempt < 2) {
              await new Promise((r) => setTimeout(r, 400));
              await firebaseUser.reload();
              continue;
            }
            break;
          }
        } catch (err) {
          console.error("Failed to sync user:", err);
        } finally {
          setUser(firebaseUser);
          setLoading(false);
        }
      } else {
        setUser(null);
        setIsAdmin(false);
        setHasDelegateAccess(false);
        setDbDisplayName("");
        setActiveLeagueCount(0);
        setIsSuspended(false);
        setIsRegistrationClosed(false);
        setLoading(false);
      }
    });
    return unsubscribe;
  }, [setTheme]);

  const handleSignIn = (mode: AuthModalMode = "signin") => setAuthModalMode(mode);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error("Sign out failed:", err);
    }
  };

  if (loading) {
    // / is the marketing home for every visitor, signed in or not — render it
    // immediately instead of blocking on a blank spinner while Firebase
    // resolves. Its header/CTAs briefly show the logged-out state and flip
    // to the signed-in state once loading finishes, which is preferable to
    // a blank screen. Every other route still needs to know auth state
    // before it can render anything meaningful, so those keep the spinner.
    if (window.location.pathname === "/") {
      return (
        <BrowserRouter>
          <WelcomePage
            user={null}
            onSignIn={handleSignIn}
            pendingDashboardRedirect={pendingDashboardRedirect}
            onDashboardRedirectHandled={() => setPendingDashboardRedirect(false)}
          />
          {authModalMode && (
            <AuthModal
              initialMode={authModalMode}
              onClose={() => setAuthModalMode(null)}
              onAuthenticated={() => setPendingDashboardRedirect(true)}
            />
          )}
        </BrowserRouter>
      );
    }
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "var(--color-surface)" }}>
        <div className="sg-spinner" />
      </div>
    );
  }

  if (isSuspended) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "var(--color-bg)" }}>
        <div style={{ ...card, maxWidth: "440px", textAlign: "center" }}>
          <h2 style={{ color: "var(--color-danger)", marginTop: 0 }}>{t("auth.suspended.title")}</h2>
          <p style={{ color: "var(--color-text-muted)", lineHeight: 1.6 }}>
            {t("auth.suspended.body")}
          </p>
          <button onClick={handleSignOut} style={{ ...secondaryBtn, marginTop: "0.5rem" }}>
            {t("auth.suspended.signOut")}
          </button>
        </div>
      </div>
    );
  }

  if (isRegistrationClosed) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "var(--color-bg)" }}>
        <div style={{ ...card, maxWidth: "440px", textAlign: "center" }}>
          <h2 style={{ marginTop: 0, color: "var(--color-text)" }}>{t("auth.registrationClosed.title")}</h2>
          <p style={{ color: "var(--color-text-muted)", lineHeight: 1.6 }}>
            {t("auth.registrationClosed.body")}
          </p>
          <WaitlistForm source="registration_closed" />
          <button onClick={handleSignOut} style={{ ...secondaryBtn, marginTop: "1.25rem" }}>
            {t("auth.registrationClosed.signOut")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <BreadcrumbProvider>
      <Layout
        user={user}
        isAdmin={isAdmin}
        hasDelegateAccess={hasDelegateAccess}
        displayName={dbDisplayName}
        onSignIn={handleSignIn}
        onSignOut={handleSignOut}
        previewMode={previewMode}
      >
        <Routes>
          {/* / is the marketing home for every visitor — signed in or not.
              The signed-in dashboard lives at /overview instead. */}
          <Route
            path="/"
            element={
              <WelcomePage
                user={user}
                onSignIn={handleSignIn}
                pendingDashboardRedirect={pendingDashboardRedirect}
                onDashboardRedirectHandled={() => setPendingDashboardRedirect(false)}
              />
            }
          />
          <Route
            path="/overview"
            element={
              user ? (
                <HomePage
                  user={user}
                  displayName={dbDisplayName}
                  onActiveLeagueCountChange={setActiveLeagueCount}
                />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/leagues/create"
            element={
              user ? <CreateLeaguePage user={user!} /> : <Navigate to="/" />
            }
          />
          <Route
            path="/archives/:id"
            element={user ? <ArchivePage user={user!} /> : <Navigate to="/" />}
          />
          <Route
            path="/leagues/join"
            element={user ? <JoinLeaguePage user={user!} /> : <Navigate to="/" />}
          />
          {/* Public — a shared invite link must work for a logged-out visitor. Unlike every
              other route here, deliberately not gated on `user`. */}
          <Route path="/join/:code" element={<PublicJoinPage user={user} />} />
          <Route
            path="/leagues/:id"
            element={user ? <LeaguePage user={user!} /> : <Navigate to="/" />}
          />
          <Route
            path="/leagues/:id/teams/:memberId"
            element={user ? <TeamPage user={user!} isAdmin={isAdmin} /> : <Navigate to="/" />}
          />
          <Route
            path="/leagues/:id/predict"
            element={user ? <PredictionPage user={user!} /> : <Navigate to="/" />}
          />
          <Route
            path="/leagues/:id/syndicate"
            element={user ? <SyndicatePage user={user!} /> : <Navigate to="/" />}
          />
          <Route
            path="/leagues/:id/syndicate/member/:memberId"
            element={user ? <SyndicatePage user={user!} /> : <Navigate to="/" />}
          />
          <Route
            path="/leagues/:id/challenge"
            element={user ? <GwChallengePage user={user!} /> : <Navigate to="/" />}
          />
          <Route
            path="/leagues/:id/challenge/member/:memberId"
            element={user ? <GwChallengeTeamPage user={user!} /> : <Navigate to="/" />}
          />
          <Route
            path="/leagues/:id/challenge/pick/:gwId"
            element={user ? <GwPickPage user={user!} /> : <Navigate to="/" />}
          />
          <Route
            path="/leagues/:id/score-predictor"
            element={user ? <ScorePredictorPage user={user!} /> : <Navigate to="/" />}
          />
          <Route
            path="/leagues/:id/score-predictor/member/:memberId"
            element={user ? <ScorePredictorPage user={user!} /> : <Navigate to="/" />}
          />
          <Route
            path="/admin"
            element={isAdmin && !previewMode ? <AdminLayout user={user!} /> : <Navigate to="/overview" />}
          >
            <Route index element={<OpsHomePage user={user!} />} />
            <Route path="competitions" element={<CompetitionsPage user={user!} />} />
            <Route path="competitions/new" element={<CreateCompetitionPage user={user!} />} />
            <Route path="competitions/:id" element={<AdminCompetitionPage user={user!} />}>
              <Route index element={<OverviewRoute />} />
              <Route path="gameweeks" element={<GameweeksRoute />} />
              <Route path="gameweeks/:gwId" element={<GameweekOpsRoute />} />
              <Route path="entrants" element={<EntrantsRoute />} />
              <Route path="squads" element={<SquadsRoute />} />
              <Route path="modes" element={<ModesRoute />} />
              <Route path="data-source" element={<DataSourceRoute />} />
            </Route>
            <Route path="users" element={<UsersTab user={user!} />} />
            <Route path="leagues" element={<LeaguesPage user={user!} />} />
            <Route path="leagues/:leagueId" element={<LeagueDetailPage user={user!} />} />
            <Route path="clubs" element={<ClubsPage user={user!} />} />
            <Route path="metrics" element={<MetricsPage user={user!} />} />
            {process.env.REACT_APP_API_URL?.includes("localhost") && (
              <Route path="dev" element={<DevPage />} />
            )}
          </Route>
          <Route
            path="/my-gameweeks"
            element={user ? <MyGameweeksPage user={user!} /> : <Navigate to="/" />}
          />
          <Route
            path="/my-gameweeks/:competitionId/:gwId"
            element={user ? <MyGameweekOpsRoute user={user!} /> : <Navigate to="/" />}
          />
          <Route
            path="/profile"
            element={
              user ? (
                <ProfilePage
                  user={user!}
                  onDisplayNameChange={setDbDisplayName}
                />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/about" element={<AboutPage user={user} onSignIn={handleSignIn} />} />
        </Routes>
      </Layout>
      </BreadcrumbProvider>
      {authModalMode && (
        <AuthModal
          initialMode={authModalMode}
          onClose={() => setAuthModalMode(null)}
          onAuthenticated={() => setPendingDashboardRedirect(true)}
        />
      )}
    </BrowserRouter>
  );
}

function App() {
  return (
    <PreviewModeProvider>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </PreviewModeProvider>
  );
}

export default App;
